/**
 * Phase 41 Plan 41-05 Task 3 — useDashboardKpis.
 *
 * Composes the values rendered by the Dashboard KPI strip. Per checker
 * I-07, the executor MUST identify the hook field paths by reading the
 * actual hook / API files — the plan text intentionally did not
 * hard-code field names because they may have drifted since the plan
 * was authored.
 *
 * Per Open Q5 default — no new backend endpoint is added. All aggregation
 * happens client-side; loading states surface as `null` so the KpiStrip
 * can render "—" without inventing a fake zero. Each value resolves
 * independently — a slow Invoices fetch does NOT block the Hours KPI
 * from rendering once timesheets land.
 *
 * F-02 (2026-05-13 UAT batch Phase 3) — KPI strip switched from
 * "current month / current period" to LAST CALENDAR MONTH semantics so
 * the dashboard shows a stable retrospective (per the Plan 41-05 docstring
 * intent). Three of the four tiles are now last-month-bound:
 *
 *   - Hours       — sum `entry.hours` from `/time/entries/range` for the
 *                   first..last day of the previous calendar month.
 *   - Expenses    — filter `getUserExpenseSheets()` to sheets with
 *                   `state='approved'` AND `createdAt` in last month.
 *   - Invoiced    — sum `Invoice.invoicedAmount` for invoices created
 *                   in last month (was sourced from useDashboardCashflow
 *                   which is locked to the CURRENT month for the
 *                   CashflowCard semantic — splitting here so the two
 *                   surfaces don't fight).
 *
 * Time off stays as YEAR-TO-DATE remaining since the user's leave quota
 * is annual, not monthly.
 *
 * F-03 (2026-05-13 UAT batch Phase 3) — Invoiced KPI now exposes a
 * sent / uninvoiced split so the user sees both halves. Sourced from
 * the existing `/api/reports/invoicing-status` report, which buckets
 * approved billable entries into `invoicedAmount` (linked to an
 * Invoice) vs `uninvoicedAmount` (approved but not yet on an invoice).
 * That endpoint is admin / finance gated; for everyone else
 * `invoicedSplit` stays `null` and the strip renders the headline
 * value alone.
 */
import { useQuery } from '@tanstack/react-query';
import { getDateRangeEntries } from '../services/api/timeEntry';
import { getUserExpenseSheets } from '../services/api/expenses';
import { getUserTimeOffs } from '../services/api/timeOff';
import { getInvoices } from '../services/api/invoices';
import { getInvoicingStatusReport } from '../services/api/reports';
import { useAuth } from '../services/authContext';

const CURRENT_YEAR = new Date().getFullYear();
const ANNUAL_LEAVE_QUOTA = 25;

export interface InvoicedSplit {
  /** Approved billable amount that IS linked to an Invoice (i.e. "sent"). */
  invoicedAmount: number;
  /** Approved billable amount NOT yet on an invoice (i.e. "not sent"). */
  uninvoicedAmount: number;
}

export interface DashboardKpis {
  /** Total hours logged in the previous calendar month. */
  hoursThisMonth: number | null;
  /** Sum of `totalAmount` for expense sheets in state=approved with createdAt in last month. */
  approvedExpensesThisMonth: number | null;
  /** Currency display code for the expenses + invoiced KPIs. */
  expensesCurrency: string;
  /** Sum of `invoicedAmount` for invoices created in last month. */
  invoicedThisMonth: number | null;
  /** Currency display code for the invoiced KPI. */
  invoicedCurrency: string;
  /**
   * F-03 — invoiced vs uninvoiced (sent vs not-sent) split for last
   * month, when the caller has access to /reports/invoicing-status.
   * `null` for non-admin / non-finance users.
   */
  invoicedSplit: InvoicedSplit | null;
  /** Annual quota minus taken+planned approved time-off days. */
  timeOffRemainingDays: number | null;
}

/**
 * Returns ISO `YYYY-MM-DD` boundaries for the previous calendar month.
 * Example: called on 2026-05-13 → `{ start: '2026-04-01', end: '2026-04-30' }`.
 */
function lastMonthRange(): { start: string; end: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0); // day 0 = last day prev month
  const iso = (d: Date) => d.toISOString().split('T')[0]!;
  return { start: iso(start), end: iso(end) };
}

function isInLastMonth(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return d.getMonth() === lastMonth.getMonth() && d.getFullYear() === lastMonth.getFullYear();
}

export function useDashboardKpis(): DashboardKpis {
  const { isAdmin, isFinance } = useAuth();
  const range = lastMonthRange();

  // Hours — sum entries dated within the previous calendar month.
  // F-02: was `getCurrentTimesheet().totalHours` (current period only);
  // switched to `/time/entries/range` so the value reflects last month
  // even when the active period straddles a month boundary.
  const hoursQuery = useQuery({
    queryKey: ['dashboard', 'kpi', 'hours', 'lastMonth', range.start, range.end],
    queryFn: () => getDateRangeEntries(range.start, range.end),
  });
  const hoursThisMonth = hoursQuery.data
    ? hoursQuery.data.data.entries.reduce((sum, e) => sum + Number(e.hours ?? 0), 0)
    : null;

  // Approved expenses — sheets approved with createdAt in last month.
  const expenses = useQuery({
    queryKey: ['dashboard', 'kpi', 'expenses', 'lastMonth'],
    queryFn: () => getUserExpenseSheets(),
  });
  const approvedExpensesThisMonth = expenses.data
    ? expenses.data
        .filter((e) => e.state === 'approved' && isInLastMonth(e.createdAt))
        .reduce((sum, e) => sum + Number(e.totalAmount ?? 0), 0)
    : null;

  // Invoiced — sum invoicedAmount of invoices created in last month.
  // /api/invoices is admin / finance gated; everyone else's query 403s
  // and the value stays `null` (KpiStrip renders "—").
  const canViewInvoices = isAdmin || isFinance;
  const invoicesQuery = useQuery({
    queryKey: ['dashboard', 'kpi', 'invoices', 'lastMonth'],
    queryFn: () => getInvoices(),
    enabled: canViewInvoices,
  });
  const invoicedThisMonth = invoicesQuery.data
    ? invoicesQuery.data
        .filter((inv) => isInLastMonth(inv.createdAt))
        .reduce((sum, inv) => sum + Number(inv.invoicedAmount ?? 0), 0)
    : null;

  // F-03 — invoiced vs uninvoiced split. Same admin / finance gate as
  // /invoicing-status; non-admin callers see the headline only.
  const splitQuery = useQuery({
    queryKey: ['dashboard', 'kpi', 'invoicedSplit', 'lastMonth', range.start, range.end],
    queryFn: () => getInvoicingStatusReport({ from: range.start, to: range.end }),
    enabled: canViewInvoices,
  });
  const invoicedSplit: InvoicedSplit | null = splitQuery.data
    ? {
        invoicedAmount: Number(splitQuery.data.totals.invoicedAmount ?? 0),
        uninvoicedAmount: Number(splitQuery.data.totals.uninvoicedAmount ?? 0),
      }
    : null;

  // Time off remaining — annual quota minus approved (taken + planned).
  // Stays YEAR-TO-DATE — leave quotas are annual, not monthly.
  const timeOffs = useQuery({
    queryKey: ['dashboard', 'kpi', 'approvedTimeOffs', CURRENT_YEAR],
    queryFn: () => getUserTimeOffs({ year: CURRENT_YEAR, state: 'approved' }),
  });
  const timeOffRemainingDays = timeOffs.data
    ? Math.max(
        0,
        ANNUAL_LEAVE_QUOTA -
          timeOffs.data.reduce((sum, t) => sum + Number(t.totalDays ?? 0), 0),
      )
    : null;

  return {
    hoursThisMonth,
    approvedExpensesThisMonth,
    expensesCurrency: 'CZK',
    invoicedThisMonth,
    invoicedCurrency: 'CZK',
    invoicedSplit,
    timeOffRemainingDays,
  };
}
